import { useState, useRef, ChangeEvent, DragEvent } from 'react';
import invitationService from '../../services/invitation.service';
import { BulkInviteResult } from '../../types/invitation.types';

// Phase 5 - US3: Participant Upload Component (T099)

interface ParticipantUploadProps {
  surveyId: string;
  onSuccess?: () => void;
}

const ParticipantUpload = ({ surveyId, onSuccess }: ParticipantUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<BulkInviteResult | null>(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    setError('');
    setResult(null);

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      setError('Por favor, selecione um arquivo CSV');
      return;
    }

    // Validate file size (5MB max)
    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('O arquivo não pode exceder 5MB');
      return;
    }

    setFile(selectedFile);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      const uploadResult = await invitationService.uploadCSVAndInvite(surveyId, file);
      setResult(uploadResult);

      if (uploadResult.queued > 0 && onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Falha ao enviar convites');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = 'email,phone,name\nexample@email.com,+5511999999999,John Doe\n';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-4">Enviar Convites</h2>

      {/* File Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="hidden"
        />

        {!file ? (
          <>
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              Arraste e solte seu arquivo CSV aqui, ou
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Selecionar Arquivo
            </button>
            <p className="mt-2 text-xs text-gray-500">
              CSV até 5MB. Colunas: email, phone (pelo menos um obrigatório), name (opcional)
            </p>
          </>
        ) : (
          <>
            <svg
              className="mx-auto h-12 w-12 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mt-2 text-sm font-medium text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500">
              {(file.size / 1024).toFixed(2)} KB
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploading ? 'Enviando...' : 'Enviar Convites'}
              </button>
              <button
                onClick={handleReset}
                disabled={uploading}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:bg-gray-100"
              >
                Cancelar
              </button>
            </div>
          </>
        )}
      </div>

      {/* Download Template */}
      <div className="mt-4">
        <button
          onClick={downloadTemplate}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Baixar modelo CSV
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Upload Result */}
      {result && (
        <div className="mt-6 space-y-4">
          <div className="bg-green-50 border border-green-200 rounded p-4">
            <h3 className="font-semibold text-green-800 mb-2">
              ✓ Upload Concluído
            </h3>
            <p className="text-sm text-green-700">
              {result.queued} convite(s) enviado(s) com sucesso
            </p>
            {result.failed > 0 && (
              <p className="text-sm text-orange-700 mt-1">
                {result.failed} convite(s) falharam
              </p>
            )}
          </div>

          {/* Parse Errors */}
          {result.parseErrors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">
                Erros de Validação ({result.parseErrors.length})
              </h3>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left">Linha</th>
                      <th className="text-left">Erros</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.parseErrors.map((err, idx) => (
                      <tr key={idx} className="border-t border-yellow-100">
                        <td className="py-1">{err.row}</td>
                        <td className="py-1 text-yellow-700">
                          {err.errors.join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Send Errors */}
          {result.sendErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-4">
              <h3 className="font-semibold text-red-800 mb-2">
                Erros de Envio ({result.sendErrors.length})
              </h3>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left">Destinatário</th>
                      <th className="text-left">Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sendErrors.map((err, idx) => (
                      <tr key={idx} className="border-t border-red-100">
                        <td className="py-1">
                          {err.recipient.email || err.recipient.phone}
                        </td>
                        <td className="py-1 text-red-700">{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParticipantUpload;
